import React, { useState, useEffect, Suspense } from 'react';
import ProductCard from '../components/ui/ProductCard';
import Pagination from '../components/ui/Pagination';
import { useProducts } from '../hooks/useAllProductsQueries';

const ITEMS_PER_PAGE = 10;

const BestSellersContent = () => {
  const [filters] = useState({
    categories: [],
    priceRange: { min: '', max: '' },
    sortBy: 'newest',
    brands: [],
    page: 1,
    limit: 50, // Fetch a good amount to pick random best sellers
  });

  const { data: productsData } = useProducts(filters);
  const [allProducts, setAllProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (productsData?.products) {
      const shuffled = [...productsData.products].sort(() => 0.5 - Math.random());
      setAllProducts(shuffled);
      setCurrentPage(1); // Reset to first page whenever products reload
    }
  }, [productsData?.products]);

  const totalPages = Math.ceil(allProducts.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedProducts = allProducts.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="bg-white py-4 md:py-6">
      <div className="w-full md:container mx-auto px-2 sm:px-4 py-8">
        <div className="flex items-baseline justify-between border-b border-admin-slate-200 pb-6 mb-6">
          <h1 className="text-3xl font-heading font-bold tracking-tight text-admin-slate-900">
            Best Sellers
          </h1>
          {allProducts.length > 0 && (
            <p className="text-sm text-admin-slate-500">
              Showing {startIndex + 1}–{Math.min(startIndex + ITEMS_PER_PAGE, allProducts.length)} of {allProducts.length} products
            </p>
          )}
        </div>

        {paginatedProducts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-lg text-admin-slate-500">No products available at the moment.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-6">
              {paginatedProducts.map((product) => (
                <div key={product._id} className="w-[45%] sm:w-[30%] lg:w-auto flex-shrink-0 snap-start">
                  <ProductCard product={product} />
                </div>
              ))}
            </div>

            {allProducts.length > ITEMS_PER_PAGE && (
              <div className="mt-10 flex justify-center">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const BestSellersPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Suspense
      fallback={
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      }
    >
      <BestSellersContent />
    </Suspense>
  );
};

export default BestSellersPage;
